import { Row, Col } from 'antd';
import { FormikProps, useField } from 'formik';
import { Select } from 'formik-antd';
import bankersRounding from 'bankers-rounding';
import { useCallback, useEffect } from 'react';
import { has, isNil, toNumber } from 'lodash';

import {
  FinancialInfoSchedulerModel,
  FinancialSchedulerFormValues,
  MultiRateFormScheduleValues,
} from '../FinancialInfoScheduleModal';
import FormItem from 'components/formik/FormItem';
import InputNumber from 'components/formik/InputNumber';
import {
  getFinancialWarningMessages,
  getOvertimeOptions,
} from 'utils/assignmentUtils';
import { AssignmentMultiRateViewModel, OvertimeEnum } from 'api/adminApiClient';
import { getDecimalSafeValue } from 'utils/mathUtils';
import { CustomerMspOrVmsOverviewDto } from 'api/backofficeApiClient';
import {
  DT_PAY_RATE_MULTIPLIER,
  FinancialInfoCalculator,
  OVERTIME_MULTIPLIER,
} from '../../AssignmentDataModal/FinancialInfo/calculationUtils';
import { SimplePriceAgreementW2Info } from '../../AssignmentDataModal/FinancialInfo';
import { MULTI_RATES_KEY } from 'hooks/useFinancialInfoTabs';
import { findPTOMultiRate } from 'utils/multiRates/multiRatesUtils';
import { isAdminSelector } from 'redux/selectors/authenticationSelectors';
import { useAppSelector } from 'hooks/useAppSelector';
import Checkbox from 'components/formik/Checkbox';

const FIELD_NAMES = {
  DT_PAY_RATE: 'financialInfoScheduler.dtPayRate',
  PROFIT_RATE: 'financialInfoScheduler.profitRate',
  PAY_RATE: 'financialInfoScheduler.payRate',
  BILL_RATE: 'financialInfoScheduler.billRate',
  VMS_FEE: 'financialInfoScheduler.vmsFee',
  MSP_FEE: 'financialInfoScheduler.mspFee',
  VMS_FEE_IS_OVERRIDDEN: 'financialInfoScheduler.vmsFeeIsOverridden',
  MSP_FEE_IS_OVERRIDDEN: 'financialInfoScheduler.mspFeeIsOverridden',
  OVERTIME: 'financialInfoScheduler.overtime',
  OVERTIME_BILL_RATE: 'financialInfoScheduler.overtimeBillRate',
  OVERTIME_PAY_RATE: 'financialInfoScheduler.overtimePayRate',
  DT_BILL_RATE: 'financialInfoScheduler.dtBillRate',
};

type Props = {
  formik: FormikProps<FinancialSchedulerFormValues>;
  mspOrVmsOverviewInfo?: CustomerMspOrVmsOverviewDto;
  w2InfoByWorkSiteWorkLocation?: SimplePriceAgreementW2Info;
  workStateLocation?: number;
  isW2Assignment: boolean;
  formRef: any;
  ptoMultiRate?: AssignmentMultiRateViewModel;
  initialValues?: FinancialSchedulerFormValues;
};

export default function FinancialInfoScheduleSection(props: Props) {
  const {
    w2InfoByWorkSiteWorkLocation,
    workStateLocation,
    isW2Assignment,
    formik,
    // We should use formRef instead of formik
    // to get the current state of the form during any changes in the values in the fields
    formRef,
    ptoMultiRate,
    mspOrVmsOverviewInfo,
  } = props;
  const isAdmin = useAppSelector(isAdminSelector);
  const isDisabledFields = !formik.values.isCanUpdateScheduler;
  const [vmsFeeField] = useField<number>(FIELD_NAMES.VMS_FEE);
  const [mspFeeField] = useField<number>(FIELD_NAMES.MSP_FEE);
  const [vmsFeeIsOverriddenField] = useField<boolean>(
    FIELD_NAMES.VMS_FEE_IS_OVERRIDDEN,
  );
  const [mspFeeIsOverriddenField] = useField<boolean>(
    FIELD_NAMES.MSP_FEE_IS_OVERRIDDEN,
  );

  useEffect(() => {
    has(formik.values?.financialInfoScheduler, 'profitRate') &&
      calculateProfitRateValue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vmsFeeField.value, mspFeeField.value]);

  const setFieldValues = useCallback(
    (field: string, value: any, touched: boolean) => {
      const { setFieldValue, setFieldTouched } = formik;

      setFieldValue(field, value);
      setFieldTouched(field, touched);
    },
    [formik],
  );

  useEffect(() => {
    if (
      has(formik.values?.financialInfoScheduler, 'vmsFee') &&
      !vmsFeeIsOverriddenField.value
    ) {
      setFieldValues(
        FIELD_NAMES.VMS_FEE,
        mspOrVmsOverviewInfo?.vmsFee
          ? toNumber(mspOrVmsOverviewInfo?.vmsFee)
          : 0,
        false,
      );
    }
  }, [
    vmsFeeIsOverriddenField.value,
    mspOrVmsOverviewInfo,
    setFieldValues,
    formik.values?.financialInfoScheduler,
  ]);

  useEffect(() => {
    if (
      has(formik.values?.financialInfoScheduler, 'mspFee') &&
      !mspFeeIsOverriddenField.value
    ) {
      setFieldValues(
        FIELD_NAMES.MSP_FEE,
        mspOrVmsOverviewInfo?.mspFee
          ? toNumber(mspOrVmsOverviewInfo?.mspFee)
          : 0,
        false,
      );
    }
  }, [
    mspFeeIsOverriddenField.value,
    mspOrVmsOverviewInfo,
    setFieldValues,
    formik.values?.financialInfoScheduler,
  ]);

  const calculateOvertimePayRate = () => {
    setTimeout(() => {
      if (!formRef.current) return;

      const { values } =
        formRef.current as FormikProps<FinancialSchedulerFormValues>;

      const overtimePayRate = values.financialInfoScheduler?.overtimePayRate;

      if (isNil(overtimePayRate) || overtimePayRate === OvertimeEnum.Exempt)
        return;

      const payRate = values?.financialInfoScheduler?.payRate || 0;
      const value = bankersRounding(payRate * OVERTIME_MULTIPLIER, 2);
      setFieldValues(FIELD_NAMES.OVERTIME_PAY_RATE, value, true);
    }, 0);
  };

  const calculateDTPayRate = () => {
    setTimeout(() => {
      if (!formRef.current) return;

      const { values } =
        formRef.current as FormikProps<FinancialSchedulerFormValues>;
      const { overtime, payRate } = values?.financialInfoScheduler || {};

      if (overtime === OvertimeEnum.Exempt) return;

      const value = bankersRounding((payRate || 0) * DT_PAY_RATE_MULTIPLIER, 2);
      setFieldValues(FIELD_NAMES.DT_PAY_RATE, value, true);
    }, 0);
  };

  useEffect(() => {
    has(formik.values?.financialInfoScheduler, 'profitRate') &&
      calculateProfitRateValue(false);
    // eslint-disable-next-line
  }, []);

  const financialWarningMessages = getFinancialWarningMessages({
    hasW2InfoByWorkSiteWorkLocation:
      !!w2InfoByWorkSiteWorkLocation ||
      isNil(workStateLocation) ||
      !isW2Assignment,
  });

  const financialWarningMessageExists = !!financialWarningMessages.length;

  const getSafeComputedValue = (value: number) => {
    return financialWarningMessageExists ? 0 : getDecimalSafeValue(value);
  };

  const calculatePayRatePTOMultiRateValue = (
    ptoMultiRateTouched: boolean = true,
  ) => {
    setTimeout(() => {
      if (!formRef.current) return null;

      const { values, setFieldValue, setFieldTouched } =
        formRef.current as FormikProps<FinancialSchedulerFormValues>;

      if (!values.multiRates?.length) return null;

      const { ptoMultiRateIndex, ptoMultiRateObject } = findPTOMultiRate(
        values.multiRates,
      );

      const hasPayRateCustomLogic = (
        ptoMultiRateObject as MultiRateFormScheduleValues
      )?.payCodesInfo?.[0]?.hasPayRateCustomLogic;

      if (
        ptoMultiRateIndex === -1 ||
        isNil(ptoMultiRateObject) ||
        hasPayRateCustomLogic
      )
        return null;

      const fieldName = `${MULTI_RATES_KEY}[${ptoMultiRateIndex}].payCodesInfo[0].payRate`;
      const ptoPayTypeValue = FinancialInfoCalculator.calculatePayRatePTOValue(
        values.financialInfoScheduler?.payRate,
      );

      setFieldValue(fieldName, ptoPayTypeValue);
      setFieldTouched(fieldName, ptoMultiRateTouched, false);
    }, 0);
  };

  const calculatePayRateChange = () => {
    calculateProfitRateValue();
    calculateDTPayRate();
    calculateOvertimePayRate();
  };

  const handleBasePayRateChanged = () => {
    calculatePayRateChange();
    calculatePayRatePTOMultiRateValue();
  };

  const calculateProfitRateValue = (profitRateTouched: boolean = true) => {
    setTimeout(() => {
      if (!formRef.current) return;
      const { values } =
        formRef.current as FormikProps<FinancialSchedulerFormValues>;

      const { billRate, payRate, vmsFee, mspFee } =
        values.financialInfoScheduler || {};

      // Reset profitRate if billRate ot payRate is not provided
      const shouldResetValue = isNil(billRate) || isNil(payRate);
      const profitRateValue = FinancialInfoCalculator.getProfitRateValue(
        values.financialInfoScheduler as FinancialInfoSchedulerModel,
        {
          vmsRate: vmsFee,
          mspRate: mspFee,
        },
      );

      const safeValue = getSafeComputedValue(profitRateValue);

      setFieldValues(
        FIELD_NAMES.PROFIT_RATE,
        shouldResetValue ? 0 : safeValue,
        profitRateTouched,
      );
    }, 0);
  };

  const mbpPortionFormValue = getSafeComputedValue(
    FinancialInfoCalculator.getMbpPortionValue(
      formik.values.financialInfoScheduler?.payRate,
      formik.values.financialInfoScheduler?.mbpRate,
    ),
  );

  const calculateBIllRatePTOMultiRateValue = (
    ptoMultiRateTouched: boolean = true,
  ) => {
    setTimeout(() => {
      if (!formRef.current) return null;

      const { values, setFieldValue, setFieldTouched } =
        formRef.current as FormikProps<FinancialSchedulerFormValues>;

      if (!values.multiRates?.length) return null;

      const { ptoMultiRateIndex, ptoMultiRateObject } = findPTOMultiRate(
        values.multiRates,
      );

      const hasBillRateCustomLogic = (
        ptoMultiRateObject as MultiRateFormScheduleValues
      )?.payCodesInfo?.[0]?.hasBillRateCustomLogic;

      if (
        ptoMultiRateIndex === -1 ||
        isNil(ptoMultiRateObject) ||
        hasBillRateCustomLogic
      )
        return null;

      const memberBilledIndicator = ptoMultiRate?.memberBilledIndicator;

      const fieldName = `${MULTI_RATES_KEY}[${ptoMultiRateIndex}].payCodesInfo[0].billRate`;
      const ptoPayTypeValue = FinancialInfoCalculator.calculateBillRatePTOValue(
        values.financialInfoScheduler?.billRate,
        mbpPortionFormValue,
        memberBilledIndicator,
      );

      setFieldValue(fieldName, ptoPayTypeValue);
      setFieldTouched(fieldName, ptoMultiRateTouched, false);
    }, 0);
  };

  const handleBillRateChanged = () => {
    calculateProfitRateValue();
    calculateBIllRatePTOMultiRateValue(false);
  };

  return (
    <Row gutter={40}>
      {formik.values?.financialInfoScheduler?.hasOwnProperty('mspFee') && (
        <Col xs={24} md={12}>
          <FormItem label="MSP rate *" name={FIELD_NAMES.MSP_FEE}>
            <InputNumber
              disabled={
                isDisabledFields || !isAdmin || !mspFeeIsOverriddenField.value
              }
              name={FIELD_NAMES.MSP_FEE}
              placeholder="0.00"
              className="full-width"
              precision={2}
            />
          </FormItem>
        </Col>
      )}
      {isAdmin &&
        formik.values?.financialInfoScheduler?.hasOwnProperty('mspFee') && (
          <Col xs={24} md={12}>
            <FormItem
              name={FIELD_NAMES.MSP_FEE_IS_OVERRIDDEN}
              className="checkbox-wrapper radio-alignment"
            >
              <Checkbox
                name={FIELD_NAMES.MSP_FEE_IS_OVERRIDDEN}
                disabled={isDisabledFields}
              >
                Override MSP rate
              </Checkbox>
            </FormItem>
          </Col>
        )}
      {formik.values?.financialInfoScheduler?.hasOwnProperty('vmsFee') && (
        <Col xs={24} md={12}>
          <FormItem label="VMS rate *" name={FIELD_NAMES.VMS_FEE}>
            <InputNumber
              disabled={
                isDisabledFields || !isAdmin || !vmsFeeIsOverriddenField.value
              }
              name={FIELD_NAMES.VMS_FEE}
              placeholder="0.00"
              className="full-width"
              precision={2}
            />
          </FormItem>
        </Col>
      )}
      {isAdmin &&
        formik.values?.financialInfoScheduler?.hasOwnProperty('vmsFee') && (
          <Col xs={24} md={12}>
            <FormItem
              name={FIELD_NAMES.VMS_FEE_IS_OVERRIDDEN}
              className="checkbox-wrapper radio-alignment"
            >
              <Checkbox
                name={FIELD_NAMES.VMS_FEE_IS_OVERRIDDEN}
                disabled={isDisabledFields}
              >
                Override VMS rate
              </Checkbox>
            </FormItem>
          </Col>
        )}
      {formik.values?.financialInfoScheduler?.hasOwnProperty('payRate') && (
        <>
          <Col xs={24} md={12}>
            <FormItem label="Base pay rate *" name={FIELD_NAMES.PAY_RATE}>
              <InputNumber
                disabled={isDisabledFields}
                name={FIELD_NAMES.PAY_RATE}
                placeholder="0.00"
                className="full-width"
                precision={2}
                inputPrefix={'$'}
                onValueChange={() => handleBasePayRateChanged()}
              />
            </FormItem>
          </Col>
        </>
      )}
      {formik.values?.financialInfoScheduler?.hasOwnProperty('billRate') && (
        <>
          <Col xs={24} md={12}>
            <FormItem label="Bill rate *" name={FIELD_NAMES.BILL_RATE}>
              <InputNumber
                disabled={isDisabledFields}
                name={FIELD_NAMES.BILL_RATE}
                placeholder="0.00"
                className="full-width"
                precision={2}
                inputPrefix={'$'}
                onValueChange={() => handleBillRateChanged()}
              />
            </FormItem>
          </Col>
        </>
      )}
      {(formik.values?.financialInfoScheduler?.hasOwnProperty('billRate') ||
        formik.values?.financialInfoScheduler?.hasOwnProperty('payRate')) && (
        <Col xs={24} md={12}>
          <FormItem label="Profit rate" name={FIELD_NAMES.PROFIT_RATE}>
            <InputNumber
              disabled
              allowNegative
              name={FIELD_NAMES.PROFIT_RATE}
              placeholder="0.00"
              className="full-width"
              precision={2}
            />
          </FormItem>
        </Col>
      )}
      {formik.values?.financialInfoScheduler?.hasOwnProperty('overtime') && (
        <Col xs={24} md={12}>
          <FormItem name={FIELD_NAMES.OVERTIME} label="Overtime *">
            <Select
              name={FIELD_NAMES.OVERTIME}
              placeholder="Select overtime type"
              className="full-width"
              disabled
            >
              {getOvertimeOptions().map((overtime) => (
                <Select.Option
                  key={`select-overtime-${overtime.value}`}
                  value={overtime.value || ''}
                >
                  {overtime.label}
                </Select.Option>
              ))}
            </Select>
          </FormItem>
        </Col>
      )}
      {formik.values?.financialInfoScheduler?.hasOwnProperty(
        'overtimeBillRate',
      ) && (
        <Col xs={24} md={12}>
          <FormItem
            label="OT bill rate *"
            name={FIELD_NAMES.OVERTIME_BILL_RATE}
          >
            <InputNumber
              disabled={
                isDisabledFields ||
                formik.values?.financialInfoScheduler?.overtime ===
                  OvertimeEnum.Exempt
              }
              name={FIELD_NAMES.OVERTIME_BILL_RATE}
              placeholder="0.00"
              className="full-width"
              precision={2}
              inputPrefix={'$'}
            />
          </FormItem>
        </Col>
      )}
      {formik.values?.financialInfoScheduler?.hasOwnProperty(
        'overtimePayRate',
      ) && (
        <Col xs={24} md={12}>
          <FormItem label="OT pay rate *" name={FIELD_NAMES.OVERTIME_PAY_RATE}>
            <InputNumber
              disabled={isDisabledFields}
              name={FIELD_NAMES.OVERTIME_PAY_RATE}
              placeholder="0.00"
              className="full-width"
              precision={2}
              inputPrefix={'$'}
            />
          </FormItem>
        </Col>
      )}
      {formik.values?.financialInfoScheduler?.hasOwnProperty('dtBillRate') && (
        <Col xs={24} md={12}>
          <FormItem label="DT Bill rate *" name={FIELD_NAMES.DT_BILL_RATE}>
            <InputNumber
              disabled={
                isDisabledFields ||
                formik.values?.financialInfoScheduler?.overtime ===
                  OvertimeEnum.Exempt
              }
              placeholder="0.00"
              name={FIELD_NAMES.DT_BILL_RATE}
              className="full-width"
              precision={2}
              inputPrefix={'$'}
            />
          </FormItem>
        </Col>
      )}
    </Row>
  );
}
